import { createParentEducationMockDB } from "../pages/parentRelated/parentEducation/mock/parentEducationMockData";

const delay = (ms = 120) => new Promise((resolve) => setTimeout(resolve, ms));

let mockDB = createParentEducationMockDB();
let periodSeq = 1000;

const asString = (v) => (v === null || v === undefined ? "" : String(v));
const normalizeHierarchyOneInput = (v) => {
  const s = asString(v);
  if (s === "") return "0";
  return s;
};
const normalizeSchoolId = (v) => asString(v);

const sortByOrder = (a, b) =>
  Number(a.display_order || 0) - Number(b.display_order || 0);

export default class ParentEducationService {
  constructor(http) {
    this.http = http;
    this.useMock = process.env.REACT_APP_USE_PARENT_EDU_MOCK === "true";
  }

  async getBootstrap() {
    if (!this.useMock) {
      return this.http.fetch("/api/parent-education/bootstrap", { method: "GET" });
    }
    await delay();
    return {
      periods: [...mockDB.periods].sort(sortByOrder),
      attendances: [...mockDB.attendances],
    };
  }

  async getParentEducationByUsername(params = {}) {
    if (!this.useMock) {
      const query = new URLSearchParams();
      if (params.schoolid) query.set("schoolid", String(params.schoolid));
      if (params.school_year_id)
        query.set("school_year_id", String(params.school_year_id));
      if (params.term_id) query.set("term_id", String(params.term_id));
      if (params.hierarchy_one)
        query.set("hierarchy_one", String(params.hierarchy_one));
      if (params.username) query.set("username", String(params.username));
      return this.http.fetch(
        `/api/parent-education/by-username?${query.toString()}`,
        { method: "GET" },
      );
    }

    await delay();
    const schoolid = normalizeSchoolId(params?.schoolid);
    const schoolYearId = asString(params?.school_year_id);
    const termId = asString(params?.term_id);
    const hierarchyOne = normalizeHierarchyOneInput(params?.hierarchy_one);
    const username = asString(params?.username);

    const periods = [...mockDB.periods]
      .filter((row) => {
        if (schoolid && normalizeSchoolId(row.schoolid) !== schoolid) return false;
        if (schoolYearId && asString(row.school_year_id) !== schoolYearId) return false;
        if (termId && asString(row.term_id) !== termId) return false;
        const rowHierarchy = asString(row.hierarchy_one);
        if (
          hierarchyOne &&
          hierarchyOne !== "0" &&
          rowHierarchy !== "0" &&
          rowHierarchy !== "" &&
          rowHierarchy !== hierarchyOne
        ) {
          return false;
        }
        return true;
      })
      .sort(sortByOrder);

    const periodIds = new Set(periods.map((row) => asString(row.id)));
    const attendances = [...mockDB.attendances].filter(
      (row) =>
        (!username || asString(row.username) === username) &&
        periodIds.has(asString(row.period_id)),
    );

    return { periods, attendances };
  }

  async createPeriod(body) {
    if (!this.useMock) {
      return this.http.fetch("/api/parent-education/periods", {
        method: "POST",
        body: JSON.stringify(body),
      });
    }
    await delay();
    const created = {
      ...body,
      id: `pe-${periodSeq++}`,
      schoolid: normalizeSchoolId(body.schoolid),
      school_year_id: asString(body.school_year_id),
      term_id: asString(body.term_id),
      hierarchy_one: normalizeHierarchyOneInput(body.hierarchy_one),
      display_order: Number(body.display_order || 0),
    };
    mockDB.periods.push(created);
    return created;
  }

  async updatePeriod(id, body) {
    if (!this.useMock) {
      return this.http.fetch(`/api/parent-education/periods/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
    }
    await delay();
    mockDB.periods = mockDB.periods.map((row) =>
      String(row.id) === String(id)
        ? {
            ...row,
            ...body,
            schoolid: normalizeSchoolId(body.schoolid ?? row.schoolid),
            school_year_id: asString(body.school_year_id ?? row.school_year_id),
            term_id: asString(body.term_id ?? row.term_id),
            hierarchy_one: normalizeHierarchyOneInput(
              body.hierarchy_one ?? row.hierarchy_one,
            ),
            display_order: Number(body.display_order ?? row.display_order ?? 0),
          }
        : row,
    );
    return mockDB.periods.find((x) => String(x.id) === String(id));
  }

  async deletePeriod(id) {
    if (!this.useMock) {
      return this.http.fetch(`/api/parent-education/periods/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
    }
    await delay();
    mockDB.periods = mockDB.periods.filter((row) => String(row.id) !== String(id));
    mockDB.attendances = mockDB.attendances.filter(
      (row) => String(row.period_id) !== String(id),
    );
    return { success: true };
  }

  async reorderPeriods(body) {
    if (!this.useMock) {
      return this.http.fetch("/api/parent-education/periods/reorder", {
        method: "PATCH",
        body: JSON.stringify(body),
      });
    }
    await delay();
    const orderMap = new Map(
      (body?.orders || []).map((row) => [String(row.id), Number(row.display_order || 0)]),
    );
    mockDB.periods = mockDB.periods.map((row) =>
      orderMap.has(String(row.id))
        ? { ...row, display_order: orderMap.get(String(row.id)) }
        : row,
    );
    return { success: true };
  }

  async loadPreviousPeriods(payload) {
    if (!this.useMock) {
      return this.http.fetch("/api/parent-education/periods/load-previous", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    }
    await delay();
    const schoolid = normalizeSchoolId(payload?.schoolid);
    const fromSchoolYearId = asString(payload?.fromSchoolYearId);
    const fromTermId = asString(payload?.fromTermId);
    const fromHierarchyOne = normalizeHierarchyOneInput(payload?.fromHierarchyOne);

    return mockDB.periods
      .filter(
        (row) =>
          (!schoolid || normalizeSchoolId(row.schoolid) === schoolid) &&
          asString(row.school_year_id) === fromSchoolYearId &&
          asString(row.term_id) === fromTermId &&
          (fromHierarchyOne === "0"
            ? asString(row.hierarchy_one) === "0" ||
              asString(row.hierarchy_one) === ""
            : asString(row.hierarchy_one) === fromHierarchyOne),
      )
      .sort(sortByOrder)
      .map((row) => ({ ...row }));
  }

  async upsertAttendanceBulk(payload) {
    if (!this.useMock) {
      return this.http.fetch("/api/parent-education/attendance/upsert-bulk", {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    }
    await delay();
    const records = Array.isArray(payload?.records) ? payload.records : [];
    records.forEach((row) => {
      const periodId = asString(row.period_id);
      const username = asString(row.username);
      const value = asString(row.value);
      const index = mockDB.attendances.findIndex(
        (x) =>
          asString(x.period_id) === periodId && asString(x.username) === username,
      );
      if (!value) {
        if (index >= 0) {
          mockDB.attendances.splice(index, 1);
        }
        return;
      }
      if (index >= 0) {
        mockDB.attendances[index] = {
          ...mockDB.attendances[index],
          value,
        };
      } else {
        mockDB.attendances.push({ period_id: periodId, username, value });
      }
    });
    return { success: true };
  }
}
